/**
 * The three things a non-native modal owes a keyboard user: Escape to close,
 * Tab trapped inside it, and focus handed back on close.
 *
 * Every modal in the dashboard sets `aria-modal="true"`. That attribute is a
 * promise to assistive tech that the rest of the page is inert, and screen
 * readers keep it by hiding the background from their own navigation. The
 * browser does not: without a trap, Tab still walks out into content the reader
 * refuses to announce, so the user is moving through controls they cannot hear.
 * A modal that sets aria-modal and does not trap focus is worse than one that
 * sets neither.
 *
 * Relationship to useDialogFocus: that hook solves the same problem for the
 * help popover and is mirrored in the extension as content/help/useDialogFocus.ts
 * with mirrored suites, so it is deliberately left alone here. This one differs
 * in one way that matters for wiring 10 call sites: it captures the previously
 * focused element itself rather than making every caller thread a ref to its
 * trigger, so adopting it is a ref plus one call per modal.
 *
 * Not a focus trap for nested dialogs: the innermost open modal wins, because
 * each instance listens on document and the last one mounted handles the event
 * first. That matches how the modals are actually used (a ConfirmDialog opened
 * from inside another modal), and closing the inner one restores focus into the
 * outer one.
 */
import { useEffect, useRef, type RefObject } from 'react';

/**
 * Open modals, outermost first. Only the last entry may trap focus or answer
 * Escape.
 *
 * Without this, a modal that opens a ConfirmDialog (which portals to
 * document.body, so it is NOT inside the outer modal's ref) would have two live
 * traps: the outer one sees focus land in the inner dialog, decides focus has
 * escaped, and drags it back out, making the confirm unusable. Escape would
 * likewise close both at once. Ordering between the two document listeners is
 * registration order, so without an explicit stack the winner is an accident of
 * mount order rather than a decision.
 */
const openModals: symbol[] = [];

/**
 * The element focus was on before whatever holds it now.
 *
 * `document.activeElement` cannot be trusted when this hook captures. React
 * applies `autoFocus` during the layout phase, walking children before their
 * parent's hooks, so a field carrying it beats every effect this hook could
 * declare; and a caller that focuses a control in its own effect declared ABOVE
 * the useModalA11y call wins by declaration order. Six of the twelve wired
 * modals do one or the other (ProjectsCreateModal, ProjectEditModal,
 * ClientReviewCreateModal and LiveReviewModal autofocus a field; ConfirmDialog
 * and BulkVerifyDialog focus a button in their own effect). For those, the live
 * activeElement is a node INSIDE the dialog that gets unmounted with it, so the
 * capture is already dead when restore runs and focus falls to <body>: the user
 * is dropped at the top of the page. This holds the answer for that case.
 *
 * One slot, because no modal here moves focus inside twice before the capture.
 * A stale entry is harmless: it is read only when the live answer is provably
 * wrong, and it is still checked against the document before being focused.
 */
let focusBeforeLast: HTMLElement | null = null;
let lastFocused: HTMLElement | null = null;

if (typeof document !== 'undefined') {
  document.addEventListener(
    'focusin',
    () => {
      const active = document.activeElement as HTMLElement | null;
      if (!active || active === document.body || active === lastFocused) return;
      focusBeforeLast = lastFocused;
      lastFocused = active;
    },
    true,
  );
}

/**
 * The element to hand focus back to when `dialog` closes.
 *
 * Prefers the live activeElement, which is right whenever nothing inside the
 * dialog took focus first. When focus is already inside, that node dies with
 * the dialog, so fall back to what was focused before it did.
 */
function focusTargetOutside(dialog: HTMLElement | null): HTMLElement | null {
  const active = document.activeElement as HTMLElement | null;
  if (!dialog || !active || !dialog.contains(active)) return active;
  if (focusBeforeLast && !dialog.contains(focusBeforeLast)) return focusBeforeLast;
  return null;
}

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'textarea:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

/**
 * Is this element actually rendered?
 *
 * Deliberately NOT `offsetParent !== null`, the usual shorthand. jsdom has no
 * layout engine and reports `offsetParent` as null for everything, so that
 * check silently classifies every control as hidden and the trap degrades to a
 * no-op that no unit test can see. `checkVisibility` is the accurate answer
 * where it exists (it accounts for stylesheet-driven display:none, which
 * offsetParent does not distinguish from position:fixed); elsewhere fall back
 * to the states we can read directly.
 *
 * The fallback errs towards treating an element as visible: over-including one
 * control costs a stray tab stop, whereas under-including collapses the list to
 * empty and drops the trap entirely.
 */
function isVisible(el: HTMLElement): boolean {
  if (el.hidden || el.getAttribute('aria-hidden') === 'true') return false;
  if (typeof el.checkVisibility === 'function') return el.checkVisibility();
  return el.style.display !== 'none' && el.style.visibility !== 'hidden';
}

/** Visible focusable descendants, in DOM order. */
function focusableWithin(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    (el) => isVisible(el) || el === document.activeElement,
  );
}

export function useModalA11y(
  isOpen: boolean,
  dialogRef: RefObject<HTMLElement | null>,
  onClose: () => void,
): void {
  // Stable per-instance identity for the open-modal stack.
  const idRef = useRef<symbol>();
  if (!idRef.current) idRef.current = Symbol('modal');
  const id = idRef.current;

  const isTopmost = () => openModals[openModals.length - 1] === id;

  // Register in the stack while open. Declared first so the entry exists before
  // any of the handlers below can fire.
  useEffect(() => {
    if (!isOpen) return;
    openModals.push(id);
    return () => {
      const i = openModals.lastIndexOf(id);
      if (i !== -1) openModals.splice(i, 1);
    };
  }, [isOpen, id]);

  // Escape closes. Registered on document rather than the dialog so it fires
  // even when focus has not yet landed inside (e.g. during the open frame).
  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !e.defaultPrevented && isTopmost()) onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
    // isTopmost reads a module-level stack at event time, so it needs no dep.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, onClose]);

  // Hand focus back on the open -> closed transition. The element is captured
  // when the effect runs (i.e. at open) and used in its cleanup (at close), so
  // callers do not have to thread a trigger ref through. Guarded on the node
  // still being in the document: a trigger inside a list that re-rendered while
  // the modal was open is gone, and focusing a detached node silently moves
  // focus to <body>, which is the very thing this prevents.
  //
  // Declared before the focus-placement effect below so the cheap path (read
  // the live activeElement) is still the one that answers. Declaration order is
  // NOT load-bearing any more, and must not be relied on again: it only ever
  // governed this hook's own effects, while autoFocus and a caller's own focus
  // effect both land earlier than anything declarable here. focusTargetOutside
  // is what actually makes this correct, by refusing a capture that is already
  // inside the dialog.
  useEffect(() => {
    if (!isOpen) return;
    const previouslyFocused = focusTargetOutside(dialogRef.current);
    return () => {
      if (previouslyFocused && document.contains(previouslyFocused)) {
        previouslyFocused.focus();
      }
    };
  }, [isOpen, dialogRef]);

  // Place focus inside on open, unless something in there already has it (a
  // field carrying autoFocus, which several of these modals use, would
  // otherwise be fought for focus and lose).
  useEffect(() => {
    if (!isOpen) return;
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (dialog.contains(document.activeElement)) return;
    focusableWithin(dialog)[0]?.focus();
  }, [isOpen, dialogRef]);

  // Trap Tab / Shift+Tab. Capture phase so this runs before anything in the
  // dialog can act on the same key.
  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab' || !isTopmost()) return;
      // The custom picker resumes Tab from its trigger, which sits earlier in
      // DOM order than the menu portal. Let it choose the adjacent form field.
      if (e.target instanceof Element && e.target.closest('[data-select-menu]')) return;
      const dialog = dialogRef.current;
      if (!dialog) return;
      const items = focusableWithin(dialog);
      if (items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement as HTMLElement | null;

      // Focus outside the dialog (or on either edge) wraps back inside.
      if (!active || !dialog.contains(active)) {
        e.preventDefault();
        (e.shiftKey ? last : first).focus();
        return;
      }
      if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      } else if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [isOpen, dialogRef]);

}
